const OAUTH_PENDING_RETURN_KEY = "skyadvisor_oauth_pending_return";

interface OAuthPendingReturn {
  path: string;
  timestamp: number;
}

export function storeOAuthReturnPath(path: string): void {
  const data: OAuthPendingReturn = {
    path,
    timestamp: Date.now(),
  };
  localStorage.setItem(OAUTH_PENDING_RETURN_KEY, JSON.stringify(data));
}

/**
 * Clear a pending return-path. #16: the wizard stores it BEFORE redirecting to the provider;
 * if the redirect never happens (auth-url error / blocked redirect), the stale path would
 * otherwise linger for its 10-min window and could yank the user back — so the failure branch
 * clears it.
 */
export function clearOAuthReturnPath(): void {
  localStorage.removeItem(OAUTH_PENDING_RETURN_KEY);
}

/**
 * Full-page navigation seam for OAuth redirects. Extracted so the "go to the
 * provider" action — the one line that must fire with the exact server-returned
 * URL, and only after the auth-url resolves — is spy-able in tests (jsdom cannot
 * actually navigate).
 */
export function redirectTo(url: string): void {
  window.location.assign(url);
}

/**
 * Return-path nav ONLY. The OAuth callback status is carried on the URL the server
 * redirects to (`?integration=<typeKey>&status=…`) and read at the destination page
 * (MarketingIntegrationsPage, for every OAuth connector including Meta and Google Ads) —
 * there is
 * no `?oauth`/localStorage status channel (removed: nothing on the server ever emitted
 * `?oauth`, so it never fired). This restores the caller to where it launched OAuth.
 */
export function getAndClearOAuthReturnPath(): { path: string } | null {
  try {
    const stored = localStorage.getItem(OAUTH_PENDING_RETURN_KEY);
    if (!stored) return null;

    const data: OAuthPendingReturn = JSON.parse(stored);
    localStorage.removeItem(OAUTH_PENDING_RETURN_KEY);

    const maxAge = 10 * 60 * 1000;
    if (Date.now() - data.timestamp > maxAge) return null;

    return { path: data.path };
  } catch {
    localStorage.removeItem(OAUTH_PENDING_RETURN_KEY);
    return null;
  }
}
